
import { TnbUser } from './tnb-user';
import { tap } from 'rxjs/operators';
import { firstValueFrom, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { WestnetzSidenavService } from '../common/westnetz-sidenav/westnetz-sidenav.service';
import { UserRoles } from '../../domain/model/roles.enum';
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private sideNavService = inject(WestnetzSidenavService);

    private httpClient = inject(HttpClient);

    private loggedInUser$ = new ReplaySubject<TnbUser>(1);
    private loggedInUser: TnbUser;


    constructor() {
        this.loggedInUser$
            .pipe(
                tap((user) => {
                    if (user) {
                        if (!this.isKaskadeUser(user)) {
                            console.error('not an kaskade user');
                            this.logout(); // Logout if not kaskade user
                        }
                    }
                }),
            )
            .subscribe();
    }

    public get user$(): Observable<TnbUser> {
        return this.loggedInUser$;
    }

    public get user(): TnbUser {
        return this.loggedInUser;
    }

    public setPrincipal(user: TnbUser) {
        this.loggedInUser = user;
        this.loggedInUser$.next(this.loggedInUser);
    }

    public get loginUrl(): string {
        return environment.oauth.loginUrl;
    }

    public login() {
        window.location.href = this.loginUrl;
    }

    public logout() {
        this.loggedInUser = null;
        this.sideNavService.closeRightnav();
        window.location.href = environment.oauth.logoutUrl;
    }

    public get isAuthenticated(): boolean {
        return (!!this.loggedInUser);
    }

    public containsValidRoleForThisApplication(): boolean {
        if (!this.isAuthenticated) {
            return true; // no session is invalid.
        }
        return this.checkRole([
            UserRoles.ADMIN_USER,
            UserRoles.WESTNETZ_USER,
            UserRoles.VNB_USER,
        ]);
    }

    public checkRole(allowedRoles: UserRoles | UserRoles[]): boolean {
        return this.isRoleInPermissions(Array.isArray(allowedRoles) ? allowedRoles : [
            allowedRoles,
        ], this.loggedInUser.roles);
    }

    private isRoleInPermissions(allowedRoles: UserRoles[], userRoles: any): boolean {
        if (Array.isArray(userRoles)) {
            return allowedRoles.some(role => userRoles.includes(role));
        }
        return false;
    }

    public isKaskadeUser(user: TnbUser): boolean {
        return this.isNvnb(user) || this.isWestnetzUserOrWestnetzAdmin(user);
    }

    public isNvnb(user: TnbUser) {
        return user.roles && user.roles.indexOf('TNB_KASK_rolle_vnbnutzer') > -1;
    }

    public isWestnetzAdmin(user: TnbUser) {
        return user.roles && user.roles.indexOf('TNB_KASK_rolle_wnadmin') > -1;
    }

    public isWestnetzUser(user: TnbUser) {
        return user.roles && user.roles.indexOf('TNB_KASK_rolle_wnnutzer') > -1;
    }

    public isWestnetzUserOrWestnetzAdmin(user: TnbUser) {
        return this.isWestnetzUser(user) || this.isWestnetzAdmin(user);
    }

    public async isAuthenticatedTryRespawnSession(): Promise<boolean> {
        if (!this.isAuthenticated) {
            const user = await firstValueFrom(this.httpClient.get<TnbUser | any>(environment.API_URL + '/profile'));
            this.setPrincipal(user);
        }
        return this.isAuthenticated;
    }
}
